import type { ComponentProps } from 'react';
import { Avatar } from '@chakra-ui/react';
import { MATCH_PLAYER_COLORS, type MatchPlayerColorSlot } from '../theme';

type AvatarRootProps = ComponentProps<typeof Avatar.Root>;

/**
 * Ink for initials on a light player fill. Page `text` and `bg` both flip with
 * the palette, so neither stays dark on olive and bronze chips in daylight.
 */
const MATCH_PLAYER_INK = '#1A1428';

/** Rough relative luminance for picking readable initials on a solid match-player fill. */
function prefersDarkForeground(hex: string): boolean {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return false;
  const r = Number.parseInt(normalized.slice(0, 2), 16) / 255;
  const g = Number.parseInt(normalized.slice(2, 4), 16) / 255;
  const b = Number.parseInt(normalized.slice(4, 6), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.45;
}

export function matchPlayerForeground(hex: string): string {
  return prefersDarkForeground(hex) ? MATCH_PLAYER_INK : 'white';
}

export function RiftboundPlayerAvatar({
  name,
  photoURL,
  fallback,
  size = 'sm',
  colorPalette = 'purple',
  matchPlayerSlot,
  ...rootProps
}: {
  name: string;
  photoURL?: string | null;
  fallback?: string;
  size?: AvatarRootProps['size'];
  colorPalette?: AvatarRootProps['colorPalette'];
  /** When set, fallback fill uses our match-player theme hex (not Chakra stock palettes). */
  matchPlayerSlot?: MatchPlayerColorSlot;
} & Omit<ComponentProps<typeof Avatar.Root>, 'children' | 'size' | 'colorPalette'>) {
  const displayName = name.trim() || 'Player';
  const imageSrc = photoURL?.trim() || undefined;
  const fallbackText = fallback?.trim();
  const matchAccent = matchPlayerSlot ? MATCH_PLAYER_COLORS[matchPlayerSlot] : null;

  return (
    <Avatar.Root
      size={size}
      colorPalette={matchAccent?.palette ?? colorPalette}
      {...(matchAccent
        ? {
            bg: matchAccent.hex,
            color: matchPlayerForeground(matchAccent.hex),
          }
        : {})}
      {...rootProps}
    >
      {imageSrc ? <Avatar.Image src={imageSrc} alt={displayName} /> : null}
      {fallbackText ? <Avatar.Fallback>{fallbackText}</Avatar.Fallback> : <Avatar.Fallback name={displayName} />}
    </Avatar.Root>
  );
}
